<template>
    <div :class="[wrapClass, {'opened': show}]">
        <div class="autocomplete-input-group" v-if="!dropdownMode" :class="{'autocomplete-selected': value}"><input
                type="search" v-model="searchText" v-bind="inputAttrs" ref="autocomplete" autocomplete="off"
                :class="inputAttrs.class || inputClass" :placeholder="inputAttrs.placeholder || placeholder"
                :disabled="inputAttrs.disabled || disabled" @blur="blur"
                @focus="focus" @input="inputChange" @keyup.enter="keyEnter" @keydown.tab="keyEnter" @keydown.up="keyUp"
                @keydown.down="keyDown"/>
            <slot name="input-icon"></slot>
        </div>
        <div class="autocomplete-list" v-if="show">
            <div class="autocomplete-list-item no-matches" v-if="!hasItems">
                <div class="autocomplete-item">{{noMatchesText}}</div>
            </div>
            <div class="autocomplete-list-group" v-else-if="groupOptions" v-for="group, groupIndex in internalItems"
                 :key="groupIndex">
                <div class="autocomplete-list-group-title" v-if="group.title">{{group.title}}</div>
                <div class="autocomplete-list-item" v-for="item, i in group.items" :key="i" @click="onClickItem(item)"
                     :class="{'autocomplete-item-active': getCursor(i, groupIndex) === cursor}"
                     @mouseover="cursor = getCursor(i, groupIndex)">
                    <slot :item="item">
                        <div class="autocomplete-item">{{getLabel(item)}}</div>
                    </slot>
                </div>
            </div>
            <div class="autocomplete-list-item" v-else v-for="item, i in internalItems" :key="i"
                 @click="onClickItem(item)" :class="{'autocomplete-item-active': i === cursor}" @mouseover="cursor = i">
                <slot :item="item">
                    <div class="autocomplete-item">{{getLabel(item)}}</div>
                </slot>
            </div>
        </div>
    </div>
</template>

<script>
  import utils from './utils'

  export default {
    props: {
      dropdownMode: {
        type: Boolean,
        default: false
      },
      minLen: {type: Number, default: utils.minLen},
      wait: {type: Number, default: utils.wait},
      value: null,
      getLabel: {
        type: Function,
        default: item => item
      },
      getValue: {
        type: [Function, Boolean],
        default: false
      },
      rootClass: {
        type: String,
        default: null
      },
      groupOptions: {
        type: Boolean,
        default: false
      },
      multiple: {
        type: Boolean,
        default: false
      },
      items: Array,
      focusOnCreate: {
        type: Boolean,
        default: false
      },
      noMatchesText: {
        type: String,
        default: 'No matched items...'
      },
      autoSelectOneItem: {type: Boolean, default: false},
      placeholder: String,
      inputClass: {type: String, default: 'autocomplete-input'},
      disabled: {type: Boolean, default: false},
      inputAttrs: {
        type: Object,
        default: () => {
          return {}
        }
      },
      keepOpen: {type: Boolean, default: true}
    },
    data () {
      return {
        searchText: '',
        showList: false,
        cursor: -1,
        internalItems: this.items || []
      }
    },
    computed: {
      mappedItems () {
        return this.groupOptions ? this.internalItems.reduce((acc, {items}) => [...acc, ...items], []) : this.internalItems
      },
      hasItems () {
        return !!this.internalItems.length
      },
      wrapClass () {
        return this.rootClass ? this.rootClass : 'autocomplete'
      },
      show () {
        return this.showList || this.keepOpen
      }
    },
    methods: {
      getCursor (index, groupIndex) {
        return index + (groupIndex < 1 ? 0 : this.internalItems[groupIndex - 1].items.length)
      },
      inputChange () {
        this.showList = true
        this.cursor = -1
        utils.callUpdateItems(this.searchText, this.updateItems)
        this.$emit('change', this.searchText)
      },
      updateItems () {
        this.$emit('update-items', this.searchText)
      },
      focus () {
        this.$refs.autocomplete.focus()
        this.$emit('focus', this.searchText)
        this.showList = true
      },
      blur () {
        this.$emit('blur', this.searchText)
        setTimeout(() => {
          this.showList = false
        }, 150)
      },
      onClickItem (item) {
        this.onSelectItem(item)
        this.$emit('item-clicked', item)
      },
      onSelectItem (item, isCreated) {
        if (item) {
          this.searchText = this.getValue ? this.getValue(item) : this.getLabel(item)
          if (!isCreated) {
            this.$emit('item-selected', item)
            this.clearAutocompleteText()
          }
        } else {
          this.setItems(this.items)
        }
        this.$emit('input', item)
      },
      setItems (items) {
        this.internalItems = items || []
      },
      isSelectedValue (value) {
        return this.internalItems.length === 1 && value === this.internalItems[0]
      },
      keyUp (e) {
        if (this.cursor > -1) {
          this.cursor--
          this.itemView(this.$el.getElementsByClassName('autocomplete-list-item')[this.cursor])
        }
      },
      keyDown (e) {
        if (this.cursor < this.mappedItems.length) {
          this.cursor++
          this.itemView(this.$el.getElementsByClassName('autocomplete-list-item')[this.cursor])
        }
      },
      itemView (item) {
        if (item) item.parentNode.scrollTop = item.offsetTop - 60
      },
      keyEnter (e) {
        if (this.showList && this.mappedItems[this.cursor]) {
          this.onSelectItem(this.mappedItems[this.cursor])
          this.showList = false
        } else {
          this.$emit('enter', e.target.value)
          this.showList = false
        }
      },
      clearAutocompleteText () {
        this.searchText = ''
        if (this.$refs.autocomplete) this.focus()
      }
    },
    created () {
      utils.minLen = this.minLen
      utils.wait = this.wait
      this.onSelectItem(this.value, true)
    },
    mounted () {
      if (this.focusOnCreate && !this.dropdownMode) this.focus()
    },
    watch: {
      items (newValue) {
        this.setItems(newValue)
        const item = utils.findItem(this.items, this.searchText, this.autoSelectOneItem)
        if (item) {
          this.onSelectItem(item)
          this.showList = false
        }
      },
      searchText (newValue) {
        if (!newValue) this.setItems(this.items)
      },
      value (newValue) {
        if (!this.isSelectedValue(newValue)) {
          this.searchText = this.getLabel(newValue)
        }
      }
    }
  }
</script>

<style>
    .autocomplete-list .autocomplete-list-item.no-matches:hover {
        background-color: unset;
        cursor: auto;
    }
</style>



// WEBPACK FOOTER //
// node_modules/lei-shared/autocomplete/Autocomplete.vue